// src/components/notify/console/NotifyAudienceBuilder.js
// Audience builder: quick groups, roles (incl. inspector / contractor) with live counts, teams,
// and individual people (search). Value: { roles: string[], teamIds: number[], users: {id,name,role}[], all: boolean }
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
    Badge, Box, Button, Flex, HStack, Icon, Input, InputGroup, InputLeftElement, SimpleGrid, Skeleton, Spinner, Tag,
    TagCloseButton, TagLabel, Text, Tooltip, useColorModeValue, VStack, Wrap, WrapItem,
} from '@chakra-ui/react';
import { MdGroups, MdPersonAdd, MdPublic, MdSearch } from 'react-icons/md';
import * as api from '../../../services/notifyApi';
import { roleMeta } from '../../../config/roles';

export const EMPTY_AUDIENCE = Object.freeze({ roles: [], teamIds: [], users: [], all: false });
export const audienceToSpec = (a) => ({
    roles: a.roles,
    teamIds: a.teamIds,
    userIds: a.users.map(u => u.id),
    all: a.all,
});
export const audienceIsEmpty = (a) => !a.all && !a.roles.length && !a.teamIds.length && !a.users.length;

function NotifyAudienceBuilder({ value, onChange, canEveryone = false, recipients = null, byRole = null }) {
    const card = useColorModeValue('white', 'gray.800');
    const border = useColorModeValue('gray.200', 'gray.700');
    const activeBorder = useColorModeValue('purple.400', 'purple.300');
    const activeBg = useColorModeValue('purple.50', 'whiteAlpha.100');
    const muted = useColorModeValue('gray.500', 'gray.400');
    const listBg = useColorModeValue('white', 'gray.700');
    const [options, setOptions] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    useEffect(() => {
        const ctrl = new AbortController();
        api.getAudienceOptions('', ctrl.signal)
            .then(o => { if (isMounted.current) setOptions(o); })
            .catch(() => {})
            .finally(() => { if (isMounted.current) setLoading(false); });
        return () => ctrl.abort();
    }, []);

    useEffect(() => {
        const term = search.trim();
        if (term.length < 2) { setResults([]); return undefined; }
        const ctrl = new AbortController();
        const t = setTimeout(() => {
            setSearching(true);
            api.getAudienceOptions(term, ctrl.signal)
                .then(o => { if (isMounted.current) setResults(o.users || []); })
                .catch(() => {})
                .finally(() => { if (isMounted.current) setSearching(false); });
        }, 250);
        return () => { clearTimeout(t); ctrl.abort(); };
    }, [search]);

    const toggleRole = useCallback((id) => {
        onChange({ ...value, all: false, roles: value.roles.includes(id) ? value.roles.filter(r => r !== id) : [...value.roles, id] });
    }, [value, onChange]);
    const toggleTeam = useCallback((id) => {
        onChange({ ...value, all: false, teamIds: value.teamIds.includes(id) ? value.teamIds.filter(t => t !== id) : [...value.teamIds, id] });
    }, [value, onChange]);
    const addUser = useCallback((u) => {
        if (value.users.some(x => x.id === u.id)) return;
        onChange({ ...value, all: false, users: [...value.users, u] });
        setSearch('');
        setResults([]);
    }, [value, onChange]);
    const removeUser = useCallback((id) => onChange({ ...value, users: value.users.filter(u => u.id !== id) }), [value, onChange]);
    const setEveryone = useCallback(() => onChange(value.all ? EMPTY_AUDIENCE : { ...EMPTY_AUDIENCE, all: true }), [value, onChange]);

    const quick = useMemo(() => {
        if (!options) return [];
        const roleCards = options.roles.filter(r => ['inspector', 'contractor', 'tech', 'supervisor'].includes(r.id))
            .map(r => ({ id: r.id, label: `${r.label}s`, count: r.active, color: r.color, description: r.description }));
        const groupCards = options.groups.map(g => ({ id: g.id, label: g.label, count: g.active, color: 'purple', description: g.roles.map(x => roleMeta(x).label).join(', ') }));
        return [...groupCards.slice(0, 2), ...roleCards];
    }, [options]);

    const renderCard = (item) => {
        const active = value.roles.includes(item.id);
        return (
            <Box key={item.id} as="button" type="button" onClick={() => toggleRole(item.id)} textAlign="left" bg={active ? activeBg : card} borderWidth="2px"
                borderColor={active ? activeBorder : border} borderRadius="xl" p={3} transition="all .15s" _hover={{ borderColor: activeBorder }}
                aria-pressed={active} title={item.description}>
                <Flex justify="space-between" align="center" gap={2}>
                    <Text fontSize="sm" fontWeight="700" noOfLines={1}>{item.label}</Text>
                    <Badge colorScheme={item.color} borderRadius="full">{item.count}</Badge>
                </Flex>
                <Text fontSize="xs" color={muted} noOfLines={1}>{item.description}</Text>
            </Box>
        );
    };

    return (
        <VStack align="stretch" spacing={3}>
            {loading ? <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>{[0, 1, 2, 3, 4, 5].map(i => <Skeleton key={i} h="58px" borderRadius="xl" />)}</SimpleGrid> : (
                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={2}>
                    {quick.map(renderCard)}
                    {canEveryone && (
                        <Box as="button" type="button" onClick={setEveryone} textAlign="left" bg={value.all ? activeBg : card} borderWidth="2px"
                            borderColor={value.all ? 'red.400' : border} borderRadius="xl" p={3} _hover={{ borderColor: 'red.300' }} aria-pressed={value.all}>
                            <HStack justify="space-between"><HStack spacing={1}><Icon as={MdPublic} color="red.400" /><Text fontSize="sm" fontWeight="700">Everyone</Text></HStack></HStack>
                            <Text fontSize="xs" color={muted}>All active staff accounts</Text>
                        </Box>
                    )}
                </SimpleGrid>
            )}

            {options && !value.all && (
                <Box>
                    <Text fontSize="xs" fontWeight="700" color={muted} mb={1}>ROLES</Text>
                    <Wrap spacing={1.5}>
                        {options.roles.map(r => {
                            const active = value.roles.includes(r.id);
                            return (
                                <WrapItem key={r.id}>
                                    <Tooltip label={r.description} hasArrow openDelay={400}>
                                        <Button size="xs" borderRadius="full" variant={active ? 'solid' : 'outline'} colorScheme={r.color} onClick={() => toggleRole(r.id)} aria-pressed={active}>
                                            {r.label} · {r.active}
                                        </Button>
                                    </Tooltip>
                                </WrapItem>
                            );
                        })}
                    </Wrap>
                </Box>
            )}

            {options?.teams?.length > 0 && !value.all && (
                <Box>
                    <Text fontSize="xs" fontWeight="700" color={muted} mb={1}>TEAMS</Text>
                    <Wrap spacing={1.5}>
                        {options.teams.map(t => {
                            const active = value.teamIds.includes(t.id);
                            return (
                                <WrapItem key={t.id}>
                                    <Button size="xs" borderRadius="full" leftIcon={<MdGroups />} variant={active ? 'solid' : 'outline'} colorScheme="teal" onClick={() => toggleTeam(t.id)} aria-pressed={active}>
                                        {t.name} · {t.members}
                                    </Button>
                                </WrapItem>
                            );
                        })}
                    </Wrap>
                </Box>
            )}

            {!value.all && (
                <Box position="relative">
                    <Text fontSize="xs" fontWeight="700" color={muted} mb={1}>PEOPLE</Text>
                    <InputGroup size="sm">
                        <InputLeftElement pointerEvents="none">{searching ? <Spinner size="xs" /> : <Icon as={MdSearch} color={muted} />}</InputLeftElement>
                        <Input placeholder="Search by name, username or email" value={search} onChange={(e) => setSearch(e.target.value)} borderRadius="lg" aria-label="Search people" />
                    </InputGroup>
                    {results.length > 0 && (
                        <Box position="absolute" zIndex="dropdown" mt={1} w="100%" bg={listBg} borderWidth="1px" borderColor={border} borderRadius="lg" boxShadow="lg" maxH="220px" overflowY="auto" role="listbox">
                            {results.map(u => (
                                <Flex key={u.id} as="button" type="button" w="100%" px={3} py={2} align="center" justify="space-between" _hover={{ bg: activeBg }} onClick={() => addUser(u)} role="option">
                                    <HStack spacing={2}><Icon as={MdPersonAdd} color={muted} /><Text fontSize="sm">{u.name}</Text><Text fontSize="xs" color={muted}>@{u.username}</Text></HStack>
                                    <Badge colorScheme={roleMeta(u.role).color}>{roleMeta(u.role).short}</Badge>
                                </Flex>
                            ))}
                        </Box>
                    )}
                    {value.users.length > 0 && (
                        <Wrap mt={2} spacing={1.5}>
                            {value.users.map(u => (
                                <WrapItem key={u.id}>
                                    <Tag size="sm" borderRadius="full" colorScheme={roleMeta(u.role).color} variant="subtle">
                                        <TagLabel>{u.name}</TagLabel><TagCloseButton onClick={() => removeUser(u.id)} aria-label={`Remove ${u.name}`} />
                                    </Tag>
                                </WrapItem>
                            ))}
                        </Wrap>
                    )}
                </Box>
            )}

            {recipients !== null && (
                <HStack spacing={2} flexWrap="wrap">
                    <Text fontSize="sm" fontWeight="700">{recipients} recipient{recipients === 1 ? '' : 's'}</Text>
                    {byRole && Object.entries(byRole).map(([r, c]) => <Badge key={r} colorScheme={roleMeta(r).color} variant="subtle">{c} {roleMeta(r).short}</Badge>)}
                </HStack>
            )}
        </VStack>
    );
}

export default memo(NotifyAudienceBuilder);
